import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import securityApi from '../api/securityApi';
import SidebarLayout from '../components/SidebarLayout';

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ severity: null, tag: null });
  const [page, setPage] = useState(0);
  const postsPerPage = 12;

  useEffect(() => {
    fetchBlogPosts();
  }, [filter, page]);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await securityApi.getBlogPosts(
        postsPerPage,
        page * postsPerPage,
        filter.severity,
        filter.tag
      );

      setPosts(response.posts || []);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-50 text-red-700',
      high: 'bg-orange-50 text-orange-700',
      medium: 'bg-yellow-50 text-yellow-700',
      low: 'bg-blue-50 text-blue-700',
    };
    return colors[severity?.toLowerCase()] || 'bg-gray-50 text-gray-700';
  };

  const handlePostClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const handleFilterChange = (filterType, value) => {
    setFilter(prev => ({ ...prev, [filterType]: value }));
    setPage(0); // Reset to first page when filtering
  };

  return (
    <SidebarLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-10">
          <div className="text-center">
            <h1 className="text-5xl font-extrabold text-gray-900 mb-4 bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
              Security Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Learn from real security vulnerabilities and how we fixed them
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto mb-8 flex flex-wrap gap-4">
          <select
            value={filter.severity || ''}
            onChange={(e) => handleFilterChange('severity', e.target.value || null)}
            className="px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white font-medium text-gray-700 hover:border-gray-300 cursor-pointer"
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <input
            type="text"
            placeholder="Filter by tag..."
            value={filter.tag || ''}
            onChange={(e) => handleFilterChange('tag', e.target.value || null)}
            className="flex-1 px-5 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white font-medium text-gray-700 placeholder-gray-400"
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="max-w-7xl mx-auto text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-gray-600">Loading blog posts...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="max-w-7xl mx-auto bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Blog Posts Grid */}
        {!loading && !error && (
          <div className="max-w-7xl mx-auto">
            {posts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow">
                <p className="text-gray-600 text-lg">No blog posts found.</p>
                <p className="text-gray-500 mt-2">Check back soon for security insights!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    onClick={() => handlePostClick(post.slug)}
                    className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100 hover:border-teal-200 transform hover:-translate-y-1"
                  >
                    {/* Colored top border based on severity */}
                    <div className={`h-2 ${
                      post.severity === 'critical' ? 'bg-red-500' :
                      post.severity === 'high' ? 'bg-orange-500' :
                      post.severity === 'medium' ? 'bg-yellow-500' :
                      'bg-blue-500'
                    }`}></div>

                    {/* Post Header */}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${getSeverityColor(post.severity)} border-2 ${
                          post.severity === 'critical' ? 'border-red-300' :
                          post.severity === 'high' ? 'border-orange-300' :
                          post.severity === 'medium' ? 'border-yellow-300' :
                          'border-blue-300'
                        }`}>
                          {post.severity}
                        </span>
                        {post.read_time_minutes && (
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {post.read_time_minutes} min
                          </span>
                        )}
                      </div>

                      <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-teal-600 transition-colors">
                        {post.title}
                      </h2>

                      <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {post.summary}
                      </p>

                      {/* Tags */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-gradient-to-r from-teal-50 to-teal-100 text-teal-700 text-xs rounded-full font-medium hover:from-teal-100 hover:to-teal-200 transition-colors"
                            >
                              #{tag}
                            </span>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="text-xs text-gray-500 self-center font-medium">
                              +{post.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          {post.author && (
                            <>
                              <div className="w-7 h-7 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                                {post.author[0].toUpperCase()}
                              </div>
                              <span className="text-sm text-gray-700 font-medium">
                                {post.author}
                              </span>
                            </>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 font-medium">
                          {new Date(post.published_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {posts.length === postsPerPage && (
              <div className="mt-12 flex justify-center gap-4">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-8 py-3 bg-teal-600 text-white rounded-xl disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-teal-700 transition-all font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <span className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold flex items-center">
                  Page {page + 1}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  className="px-8 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center gap-2"
                >
                  Next
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default BlogPage;
